import {useMemo, useState} from 'react';

import type {CalendarHeatmapDay} from 'shells/calendar/calendar-heatmap/types';
import {Stack} from 'shells/layout/stack/stack';

import {AvailabilityMatrixHeatmap} from './availability-matrix-heatmap';
import {DailyPerformanceSummaryModal} from './daily-performance-summary/daily-performance-summary-modal';
import type {DailyPerformanceSummaryDateData} from './daily-performance-summary/daily-performance-summary-modal';
import {useGetAvailabilityReport} from './graphql/hooks/use-get-availability-report';
import {getDayDataByDate} from './helpers';
import {MeetingReportHeader} from './meeting-report-header';
import {SchedulerFeatureGatePage} from './scheduler-feature-gate-page';
import {StatsSection} from './stats-section';
import {ViewBookingHeatmap} from './view-booking-heatmap';

interface Props {
    meetingId: string;
    hideHeader?: boolean;
}

export function MeetingReport(props: Props) {
    const {meetingId, hideHeader = false} = props;
    const [selectedDay, setSelectedDay] = useState<DailyPerformanceSummaryDateData | null>(null);

    const {report, isLoading} = useGetAvailabilityReport(
        meetingId,
        undefined,
        !NutClientConfig.canUseScheduler
    );

    // Index days by date for quick lookup when a heatmap date is clicked
    const daysByDate = useMemo(() => {
        const days = report?.days ?? [];
        return new Map(days.map((day) => [day.date, day] as const));
    }, [report]);

    const onDateClick = (day: CalendarHeatmapDay) => {
        const dateData = getDayDataByDate(daysByDate.get(day.date) ?? null);
        if (dateData) {
            setSelectedDay(dateData);
        } else {
            setSelectedDay(null);
        }
    };

    if (!NutClientConfig.canUseScheduler) {
        return <SchedulerFeatureGatePage />;
    }

    return (
        <Stack spacing={24}>
            {!hideHeader && <MeetingReportHeader />}
            <StatsSection meetingId={meetingId} />
            <ViewBookingHeatmap report={report} onDateClick={onDateClick} isLoading={isLoading} />
            <AvailabilityMatrixHeatmap report={report} isLoading={isLoading} />
            {selectedDay && (
                <DailyPerformanceSummaryModal
                    isOpen={true}
                    onClose={() => {
                        setSelectedDay(null);
                    }}
                    dateData={selectedDay}
                />
            )}
        </Stack>
    );
}
